import { useMemo } from 'react';
import { getActingAs, isActingAs } from '../auth/authUtils';

/**
 * Hook that returns permission helpers for the current user.
 * When acting as a creator (delegated access), checks against granted permissions.
 * When logged in as yourself, all permissions are granted.
 */
export function usePermissions() {
  const actingAs = useMemo(() => {
    if (!isActingAs()) return null;
    return getActingAs();
  }, []);

  const isDelegated = !!actingAs;
  const isAdmin = actingAs?.role === 'admin';
  const permissions = actingAs?.permissions || [];

  /** Check if user has a specific permission */
  const hasPermission = (perm) => {
    if (!isDelegated) return true; // Own account — full access
    if (isAdmin) return true; // Admin role — full access
    return permissions.includes(perm);
  };

  /** Check if user has ANY of the given permissions */
  const hasAnyPermission = (...perms) => {
    if (!isDelegated || isAdmin) return true;
    return perms.some((p) => permissions.includes(p));
  };

  /** Check if user has ALL of the given permissions */
  const hasAllPermissions = (...perms) => {
    if (!isDelegated || isAdmin) return true;
    return perms.every((p) => permissions.includes(p));
  };

  return {
    isDelegated,
    permissions,
    roleName: actingAs?.role || null,
    roleColor: actingAs?.role_color || null,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
  };
}

/**
 * Non-hook version for use outside React components (e.g. route config).
 */
export function checkPermission(perm) {
  if (!isActingAs()) return true;
  const actingAs = getActingAs();
  if (actingAs?.role === 'admin') return true;
  return (actingAs?.permissions || []).includes(perm);
}

export function checkAnyPermission(...perms) {
  if (!isActingAs()) return true;
  const actingAs = getActingAs();
  if (actingAs?.role === 'admin') return true;
  const userPerms = actingAs?.permissions || [];
  return perms.some((p) => userPerms.includes(p));
}
